/**
 * Per-row processing pipeline for the upload queue.
 *
 * Wraps the existing knowledge/documentUpload helpers so the queue's
 * worker pool can drive them through the queue's status states. The
 * factory shape lets the panel plug context (siteId / reportId) without
 * the queue itself knowing about SiteDocument shape.
 *
 * Stages:
 *   extracting  → extractTextFromFile
 *   summarizing → summarizeDocument (Gemini, with filename-fallback inside
 *                                    summarizeDocument itself)
 *   uploading   → saveDocument (POST /api/storage/save + upload-file +
 *                               Firestore metadata via authedFetch)
 *
 * Errors propagate to the queue's runOne, which handles attempts++ and
 * the failed → failed-permanent transition. Don't catch here.
 */
import type { SiteDocument } from '../../types/siteRag';
import type { ProcessRowFn } from './uploadQueue';
import type { SaveDocumentOptions } from '../knowledge/documentUpload';

const MAX_TEXT_LENGTH = 10_000;

export interface UploadProcessorOptions {
  /** Site / report context — propagated to the SiteDocument so existing
   *  consumers that filter docs by parent stay happy. */
  siteId?: string;
  reportId?: string;
  /** Override the underlying helpers for unit tests. */
  extractTextFromFile?: (file: File) => Promise<string>;
  summarizeDocument?: (
    text: string,
    fileName: string,
    archetype?: unknown,
    opts?: { signal?: AbortSignal; throwOnAbortOrTimeout?: boolean; timeoutMs?: number },
  ) => Promise<{ summary: string; tags: string[]; deferred?: boolean }>;
  /** Optional topic indexing stage — when provided or `enableTopicIndexing` is true, runs in `indexing` stage. */
  segmentDocumentTopics?: (
    doc: SiteDocument,
    opts?: { abortSignal?: AbortSignal; timeoutMs?: number },
  ) => Promise<{
    segments: NonNullable<SiteDocument['topicSegments']>;
    topics: NonNullable<SiteDocument['topics']>;
  } | null>;
  enableTopicIndexing?: boolean;
  /** Summarize deadline in ms (default 15_000, or 30_000 for .pptx/.xlsx). */
  summarizeTimeoutMs?: number;
  /** Topic index deadline in ms (default 45_000). */
  topicIndexTimeoutMs?: number;
  saveDocument?: (
    siteDoc: SiteDocument,
    rawFile?: File,
    siteId?: string,
    opts?: SaveDocumentOptions,
  ) => Promise<{
    rawUploadWarning?: { kind?: string; message?: string } | unknown;
    rawBytesVerified?: boolean;
    rawStorageSizeBytes?: number;
    rawStorageSha256?: string;
  } | void>;
}

export function createUploadProcessor(opts: UploadProcessorOptions = {}): ProcessRowFn {
  return async (row, file, { setStatus, updateProgress, signal }) => {
    const throwIfAborted = () => {
      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
    };
    throwIfAborted();

    // Persist a stable docId on the row before any stage runs so retries or
    // reload+resume reuse the exact same document ID without creating duplicate records.
    const stableDocId = row.docId || `doc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    if (!row.docId) {
      await updateProgress?.({ docId: stableDocId });
    }

    // ── extracting ────────────────────────────────────────────────
    await setStatus('extracting');
    throwIfAborted();
    const extractFn = opts.extractTextFromFile
      ?? (await import('../knowledge/documentUpload')).extractTextFromFile;
    const text = await extractFn(file);
    throwIfAborted();

    // ── summarizing (optional enrichment — timeout degrades to deferred) ──
    await setStatus('summarizing');
    const deferredStages = new Set<'summarization' | 'topic-indexing' | 'classification'>();
    const lowerName = file.name.toLowerCase();
    const isLargeFormat = lowerName.endsWith('.pptx') || lowerName.endsWith('.xlsx');
    const summarizeTimeoutMs = opts.summarizeTimeoutMs ?? (isLargeFormat ? 30_000 : 15_000);
    const summarizeController = new AbortController();
    const onAbortSummarize = () => summarizeController.abort(signal?.reason);
    if (signal) {
      signal.addEventListener('abort', onAbortSummarize, { once: true });
    }
    let summarizeTimer: ReturnType<typeof setTimeout> | undefined;
    let summary = '';
    let tags: string[] = [];
    try {
      const summarizeFn = opts.summarizeDocument
        ?? (await import('../knowledge/driveConnector')).summarizeDocument;
      const result = await Promise.race([
        summarizeFn(text, file.name, undefined, {
          signal: summarizeController.signal,
          throwOnAbortOrTimeout: true,
          timeoutMs: summarizeTimeoutMs,
        }),
        new Promise<never>((_, reject) => {
          summarizeTimer = setTimeout(() => {
            summarizeController.abort(new Error('summarize timeout'));
            reject(new Error('summarize timeout'));
          }, summarizeTimeoutMs);
        }),
      ]);
      summary = result.summary;
      tags = result.tags;
      if (result.deferred) {
        deferredStages.add('summarization');
      }
    } catch {
      throwIfAborted();
      deferredStages.add('summarization');
      const { deriveSummaryFromFilename, deriveTagsFromFilename } = await import('../knowledge/driveConnector');
      tags = deriveTagsFromFilename(file.name);
      const trimmedText = text?.trim() ?? '';
      if (trimmedText.length >= 50 && !trimmedText.startsWith('[Extraction failed:')) {
        const sentenceMatch = trimmedText.split(/(?<=[.!?])\s/)[0];
        const firstSentence = sentenceMatch && sentenceMatch.length > 0 ? sentenceMatch : trimmedText.slice(0, 200);
        const head = firstSentence.length > 300 ? `${firstSentence.slice(0, 300)}…` : firstSentence;
        summary = `${head}\n\n[Auto-summary deferred — showing first sentence of extracted text.]`;
      } else {
        summary = deriveSummaryFromFilename(file.name);
      }
    } finally {
      if (summarizeTimer) clearTimeout(summarizeTimer);
      if (signal) signal.removeEventListener('abort', onAbortSummarize);
    }
    throwIfAborted();

    // Build the SiteDocument the persistence layer expects.
    const siteDoc: SiteDocument = {
      id: stableDocId,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
       ...(row.contentSha256 ? { contentHash: row.contentSha256 } : {}),
      summary,
      tags,
      textContent: text.slice(0, MAX_TEXT_LENGTH),
      fullExtractedText: text,
      uploadedAt: new Date().toISOString(),
    };

    // ── indexing (optional when segmentDocumentTopics or enableTopicIndexing is provided) ──
    if (opts.segmentDocumentTopics || opts.enableTopicIndexing) {
      await setStatus('indexing');
      throwIfAborted();
      const topicTimeoutMs = opts.topicIndexTimeoutMs ?? 45_000;
      const topicController = new AbortController();
      const onAbortTopics = () => topicController.abort(signal?.reason);
      if (signal) {
        signal.addEventListener('abort', onAbortTopics, { once: true });
      }
      let topicTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        const segmentFn =
          opts.segmentDocumentTopics ??
          (await import('../knowledge/documentTopics')).segmentDocumentTopics;
        const indexed = await Promise.race([
          segmentFn(siteDoc, { abortSignal: topicController.signal, timeoutMs: topicTimeoutMs }),
          new Promise<null>((_, reject) => {
            topicTimer = setTimeout(() => {
              topicController.abort(new Error('topic index timeout'));
              reject(new Error('topic index timeout'));
            }, topicTimeoutMs);
          }),
        ]);
        if (indexed) {
          siteDoc.topicSegments = indexed.segments;
          siteDoc.topics = indexed.topics;
          if (indexed.topics.coverage?.windows.some(w => w.status === 'failed')) {
            deferredStages.add('topic-indexing');
          }
        } else {
          deferredStages.add('topic-indexing');
        }
      } catch {
        throwIfAborted();
        deferredStages.add('topic-indexing');
      } finally {
        if (topicTimer) clearTimeout(topicTimer);
        if (signal) signal.removeEventListener('abort', onAbortTopics);
      }
    }

    siteDoc.enrichmentStatus = deferredStages.size > 0 ? 'deferred' : 'complete';
    if (deferredStages.size > 0) {
      siteDoc.enrichmentDeferredStages = Array.from(deferredStages);
    }

    // ── uploading (durable original-byte retention required before completion) ──
    await setStatus('uploading');
    throwIfAborted();
    if (opts.saveDocument) {
      const saveResult = await opts.saveDocument(siteDoc, file);
      if (
        saveResult &&
        typeof saveResult === 'object' &&
        'rawUploadWarning' in saveResult &&
        saveResult.rawUploadWarning
      ) {
        const warning = saveResult.rawUploadWarning as { kind?: string; message?: string };
        const err = new Error(
          warning.message ||
            `Durable original-byte storage failed for "${file.name}"; refusing to claim upload success.`,
        ) as Error & { retryable?: boolean };
        if (warning.kind === 'too-large') err.retryable = false;
        throw err;
      }
    } else {
      const { saveDocument: defaultSaveDocument } = await import('../knowledge/documentUpload');
      await defaultSaveDocument(siteDoc, file, opts.siteId, {
        requireDurableOriginalBytes: true,
        uploadId: `chunk-${row.id}`,
        signal,
        abortSessionOnError: false,
        onChunkProgress: (completedChunks, totalChunks) => {
          void updateProgress?.({ chunkProgress: { completedChunks, totalChunks } });
        },
      });
    }

    return {
      docId: siteDoc.id,
      rawStoragePath: siteDoc.rawStoragePath,
      rawBytesVerified: siteDoc.rawBytesVerified ?? true,
      enrichmentStatus: siteDoc.enrichmentStatus,
      enrichmentDeferredStages: siteDoc.enrichmentDeferredStages,
    };
  };
}
